import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Button,
  InputGroup,
  Card,
  FloatingLabel,
  Badge,
} from "react-bootstrap";
import dayjs from "dayjs";

const ADMIN_EMAIL_DOMAIN = "schools.learnsmart.app";

const DEFAULT = {
  // basics
  name: "",
  code: "",
  active: true,
  plan: "free",
  strength: 0,

  // contact (top-level for services)
  email: "",
  phone: "",
  principal: "",
  website: "",

  // address (top-level for services)
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  pincode: "",

  // subscription
  subscription: { status: "trial", renewalDate: "" },

  // assets
  logoFile: null,

  // admin creds (create only)
  adminEmail: "",
  adminPassword: "",
};

const SchoolFormModal = ({ show, onHide, onSave, initial }) => {
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [touched, setTouched] = useState({});
  const fileInputRef = useRef(null);

  const codeSlug = useMemo(
    () => String(form.code || "").trim().toLowerCase(),
    [form.code]
  );
  const suggestedAdminEmail = useMemo(() => {
    if (!codeSlug) return "";
    return `${codeSlug}@${ADMIN_EMAIL_DOMAIN}`;
  }, [codeSlug]);

  const logoPreview = useMemo(() => {
    if (!form.logoFile) return null;
    return URL.createObjectURL(form.logoFile);
  }, [form.logoFile]);

  useEffect(() => {
    if (initial) {
      setForm((prev) => ({
        ...prev,
        name: initial.name || "",
        code: (initial.code || "").toUpperCase(),
        active: initial.active ?? true,
        plan: initial.plan || "free",
        strength: Number(initial.strength || 0),

        // top-level fields (compatible with services)
        email: initial.email || initial.contact?.email || "",
        phone: initial.phone || initial.contact?.phone || "",
        principal: initial.principal || initial.contact?.principal || "",
        website: initial.website || initial.contact?.website || "",

        addressLine1: initial.addressLine1 || initial.address?.addr1 || "",
        addressLine2: initial.addressLine2 || initial.address?.addr2 || "",
        city: initial.city || initial.address?.city || "",
        state: initial.state || initial.address?.state || "",
        pincode: initial.pincode || initial.address?.pincode || "",

        subscription: {
          status: initial.subscription?.status || "trial",
          renewalDate: initial.subscription?.renewalDate
            ? dayjs(initial.subscription.renewalDate).format("YYYY-MM-DD")
            : "",
        },

        logoFile: null,
        adminEmail: "",
        adminPassword: "",
      }));
    } else {
      setForm(DEFAULT);
    }
    // revoke object URLs on close
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, show]);

  const change = (path, value) => {
    setTouched((t) => ({ ...t, [path]: true }));
    if (path.startsWith("subscription.")) {
      const key = path.split(".")[1];
      setForm((f) => ({ ...f, subscription: { ...f.subscription, [key]: value } }));
    } else {
      setForm((f) => ({ ...f, [path]: value }));
    }
  };

  const generatePassword = (len = 12) =>
    Array.from(crypto.getRandomValues(new Uint32Array(len)))
      .map((n) =>
        "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%".charAt(n % 65)
      )
      .join("");

  const pwScore = (pw = "") => {
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[a-z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[@#$%]/.test(pw)) score++;
    return Math.min(score, 5);
  };

  const errMsg = (field) => {
    switch (field) {
      case "name":
        if (!form.name.trim()) return "School name is required";
        break;
      case "code":
        if (!form.code.trim()) return "School code is required";
        if (/\s/.test(form.code)) return "No spaces in code";
        break;
      case "adminEmail":
        if (!initial && form.adminEmail && !/^\S+@\S+\.\S+$/.test(form.adminEmail))
          return "Invalid email";
        break;
      case "adminPassword":
        if (!initial && form.adminPassword && form.adminPassword.length < 6)
          return "Min 6 characters";
        break;
      default:
        return "";
    }
    return "";
  };

  const isInvalid = (field) => touched[field] && !!errMsg(field);

  const submit = async (e) => {
    e.preventDefault();

    // touch required fields
    setTouched((t) => ({ ...t, name: true, code: true }));

    if (errMsg("name") || errMsg("code") || errMsg("adminEmail") || errMsg("adminPassword")) {
      return;
    }

    const upperCode = form.code.trim().toUpperCase();
    const finalAdminEmail =
      initial ? null : (form.adminEmail?.trim() || (codeSlug ? suggestedAdminEmail : "") || null);

    setSaving(true);
    try {
      await onSave({
        // basics
        name: form.name.trim(),
        code: upperCode,
        active: !!form.active,
        plan: String(form.plan || "free"),
        strength: Number(form.strength || 0),

        // top-level fields
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        principal: form.principal.trim() || null,
        website: form.website.trim() || null,

        addressLine1: form.addressLine1.trim() || null,
        addressLine2: form.addressLine2.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        pincode: form.pincode.trim() || null,

        subscription: {
          status: form.subscription.status,
          renewalDate: form.subscription.renewalDate || null, // service converts to Timestamp
        },

        logoFile: form.logoFile || null,

        // admin creds on CREATE only
        adminEmail: initial ? null : finalAdminEmail,
        adminPassword: initial ? null : (form.adminPassword || null),
      });

      onHide?.();
    } finally {
      setSaving(false);
    }
  };

  const onDropFile = (file) => {
    if (!file) return;
    if (!/image\/(png|jpeg)/.test(file.type)) return;
    change("logoFile", file);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      fullscreen="md-down"
      contentClassName="border-0 shadow-lg"
    >
      <Form onSubmit={submit}>
        {/* Header */}
        <Modal.Header closeButton className="border-0 pb-0">
          <div className="w-100">
            <div className="d-flex align-items-center justify-content-between">
              <Modal.Title className="fw-bold">
                {initial ? "Edit School" : "Add School"}
              </Modal.Title>
              <Form.Check
                type="switch"
                id="active-switch"
                checked={form.active}
                onChange={(e) => change("active", e.target.checked)}
                label={form.active ? "Active" : "Inactive"}
              />
            </div>
            <div className="text-muted small mt-1">
              Fill the details below. On create, a school admin can be provisioned automatically.
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="pt-3">
          <Row className="g-3">
            {/* Left column */}
            <Col lg={7}>
              <Card className="shadow-sm border-0">
                <Card.Body>
                  <div className="mb-3">
                    <div className="fw-semibold text-uppercase small text-muted">Basic Info</div>
                  </div>

                  <Row className="g-3">
                    <Col md={7}>
                      <FloatingLabel label="School Name *">
                        <Form.Control
                          value={form.name}
                          onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                          isInvalid={isInvalid("name")}
                          onChange={(e) => change("name", e.target.value)}
                          placeholder="Green Hill Secondary School"
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          {errMsg("name")}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Col>
                    <Col md={5}>
                      <FloatingLabel label="School Code *">
                        <Form.Control
                          value={form.code}
                          onBlur={(e) => {
                            setTouched((t) => ({ ...t, code: true }));
                            change("code", e.target.value.toUpperCase());
                          }}
                          isInvalid={isInvalid("code")}
                          onChange={(e) =>
                            change("code", e.target.value.replace(/\s+/g, ""))
                          }
                          placeholder="GHS01"
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          {errMsg("code")}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                      <div className="form-text">Unique, no spaces (e.g., ABC01)</div>
                    </Col>

                    <Col md={6}>
                      <FloatingLabel label="Email">
                        <Form.Control
                          type="email"
                          value={form.email}
                          onChange={(e) => change("email", e.target.value)}
                          placeholder="info@school.com"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col md={6}>
                      <FloatingLabel label="Phone">
                        <Form.Control
                          value={form.phone}
                          onChange={(e) => change("phone", e.target.value)}
                          placeholder="+91 90000 00000"
                        />
                      </FloatingLabel>
                    </Col>

                    <Col md={6}>
                      <FloatingLabel label="Principal">
                        <Form.Control
                          value={form.principal}
                          onChange={(e) => change("principal", e.target.value)}
                          placeholder="Dr. S Sharma"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col md={6}>
                      <FloatingLabel label="Website">
                        <Form.Control
                          type="url"
                          value={form.website}
                          onChange={(e) => change("website", e.target.value)}
                          placeholder="https://example.edu"
                        />
                      </FloatingLabel>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="shadow-sm border-0 mt-3">
                <Card.Body>
                  <div className="mb-3">
                    <div className="fw-semibold text-uppercase small text-muted">Address</div>
                  </div>

                  <Row className="g-3">
                    <Col md={12}>
                      <FloatingLabel label="Address Line 1">
                        <Form.Control
                          value={form.addressLine1}
                          onChange={(e) => change("addressLine1", e.target.value)}
                          placeholder="Street / Area"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col md={12}>
                      <FloatingLabel label="Address Line 2">
                        <Form.Control
                          value={form.addressLine2}
                          onChange={(e) => change("addressLine2", e.target.value)}
                          placeholder="Landmark / Locality"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col md={4}>
                      <FloatingLabel label="City">
                        <Form.Control
                          value={form.city}
                          onChange={(e) => change("city", e.target.value)}
                          placeholder="City"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col md={4}>
                      <FloatingLabel label="State">
                        <Form.Control
                          value={form.state}
                          onChange={(e) => change("state", e.target.value)}
                          placeholder="State"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col md={4}>
                      <FloatingLabel label="PIN Code">
                        <Form.Control
                          value={form.pincode}
                          onChange={(e) => change("pincode", e.target.value)}
                          placeholder="PIN"
                        />
                      </FloatingLabel>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="shadow-sm border-0 mt-3">
                <Card.Body>
                  <div className="mb-3 d-flex align-items-center justify-content-between">
                    <div className="fw-semibold text-uppercase small text-muted">Plan & Subscription</div>
                    <Badge bg="light" text="dark" className="border">
                      {form.plan.toUpperCase()}
                    </Badge>
                  </div>

                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Label className="small text-muted mb-1">Plan</Form.Label>
                      <div className="d-flex gap-2 flex-wrap">
                        {["free", "basic", "pro", "enterprise"].map((p) => (
                          <Button
                            key={p}
                            variant={form.plan === p ? "primary" : "outline-secondary"}
                            onClick={() => change("plan", p)}
                            size="sm"
                          >
                            {p.charAt(0).toUpperCase() + p.slice(1)}
                          </Button>
                        ))}
                      </div>
                    </Col>
                    <Col md={6}>
                      <FloatingLabel label="Strength">
                        <Form.Control
                          type="number"
                          min={0}
                          value={form.strength}
                          onChange={(e) => change("strength", e.target.value)}
                          placeholder="0"
                        />
                      </FloatingLabel>
                    </Col>

                    <Col md={6}>
                      <Form.Label className="small text-muted mb-1">Subscription Status</Form.Label>
                      <div className="d-flex gap-2 flex-wrap">
                        {["trial", "active", "expired", "canceled"].map((s) => (
                          <Button
                            key={s}
                            variant={form.subscription.status === s ? "success" : "outline-secondary"}
                            onClick={() => change("subscription.status", s)}
                            size="sm"
                          >
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                          </Button>
                        ))}
                      </div>
                    </Col>
                    <Col md={6}>
                      <FloatingLabel label="Renewal Date">
                        <Form.Control
                          type="date"
                          value={form.subscription.renewalDate || ""}
                          onChange={(e) => change("subscription.renewalDate", e.target.value)}
                          placeholder="2025-12-31"
                        />
                      </FloatingLabel>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            {/* Right column */}
            <Col lg={5}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Body>
                  <div className="mb-3">
                    <div className="fw-semibold text-uppercase small text-muted">Branding</div>
                  </div>

                  {/* Logo uploader */}
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      onDropFile(e.dataTransfer.files?.[0]);
                    }}
                    className={`border rounded-3 p-3 text-center ${dragOver ? "bg-light" : ""}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 16 }}
                      />
                    ) : (
                      <div className="text-muted">
                        <div className="mb-1 fw-semibold">Upload Logo</div>
                        <div className="small">PNG/JPG up to ~2MB</div>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg"
                      hidden
                      onChange={(e) => onDropFile(e.target.files?.[0] || null)}
                    />
                  </div>

                  {/* Admin (create only) */}
                  {!initial && (
                    <>
                      <hr className="my-4" />
                      <div className="mb-2 d-flex align-items-center justify-content-between">
                        <div className="fw-semibold text-uppercase small text-muted">School Admin</div>
                        {suggestedAdminEmail && !form.adminEmail && (
                          <Badge
                            bg="light"
                            text="dark"
                            className="border"
                            style={{ cursor: "pointer" }}
                            onClick={() => change("adminEmail", suggestedAdminEmail)}
                            title={`Use ${suggestedAdminEmail}`}
                          >
                            Use {suggestedAdminEmail}
                          </Badge>
                        )}
                      </div>

                      <FloatingLabel label="Admin Email" className="mb-3">
                        <Form.Control
                          type="email"
                          value={form.adminEmail}
                          onBlur={() => setTouched((t) => ({ ...t, adminEmail: true }))}
                          isInvalid={isInvalid("adminEmail")}
                          onChange={(e) => change("adminEmail", e.target.value)}
                          placeholder="admin@school.com"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errMsg("adminEmail")}
                        </Form.Control.Feedback>
                      </FloatingLabel>

                      <Form.Label className="mb-1">Admin Password</Form.Label>
                      <InputGroup className="mb-1">
                        <Form.Control
                          type={showPw ? "text" : "password"}
                          value={form.adminPassword}
                          onBlur={() => setTouched((t) => ({ ...t, adminPassword: true }))}
                          isInvalid={isInvalid("adminPassword")}
                          onChange={(e) => change("adminPassword", e.target.value)}
                          placeholder="Min 6 characters"
                        />
                        <Button
                          variant="outline-secondary"
                          type="button"
                          onClick={() => setShowPw((s) => !s)}
                          title={showPw ? "Hide" : "Show"}
                        >
                          <i className={`bi ${showPw ? "bi-eye-slash" : "bi-eye"}`} />
                        </Button>
                        <Button
                          variant="outline-primary"
                          type="button"
                          onClick={() => change("adminPassword", generatePassword())}
                          title="Generate"
                        >
                          Generate
                        </Button>
                        <Form.Control.Feedback type="invalid">
                          {errMsg("adminPassword")}
                        </Form.Control.Feedback>
                      </InputGroup>

                      {/* simple strength bar */}
                      {form.adminPassword && (
                        <div className="d-flex gap-1">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <div
                              key={i}
                              style={{
                                height: 6,
                                flex: 1,
                                borderRadius: 4,
                                background:
                                  i < pwScore(form.adminPassword) ? (i < 2 ? "#ffc107" : i < 4 ? "#0d6efd" : "#198754") : "#e9ecef",
                                transition: "background 160ms ease",
                              }}
                              aria-hidden
                            />
                          ))}
                        </div>
                      )}
                      <div className="form-text mt-1">
                        Prefer a strong, unique password. You can also send a password reset later from Manage Schools.
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0" style={{ position: "sticky", bottom: 0 }}>
          <div className="d-flex w-100 justify-content-between align-items-center">
            <div className="text-muted small">
              {initial ? "Editing existing school" : "A new school will be created"}
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Form>

      {/* Inline polish (kept tiny) */}
      <style>{`
        .modal-content { border-radius: 16px; }
        .card { border-radius: 14px; }
        .form-control, .form-select { border-radius: 10px; }
      `}</style>
    </Modal>
  );
};

export default SchoolFormModal;
